import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Wand2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODELS = [
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { id: "claude-sonnet-5", label: "Claude Sonnet 5" },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
];

export function SupportDraft({ onApply }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (prompt.trim().length < 3) {
      toast.error("Tell the assistant a little more first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/claude/draft`, { prompt, model });
      onApply({ subject: data.subject, message: data.message });
      toast.success("Draft ready — review and edit before sending.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't generate a draft. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="support-draft"
      className="mb-6 rounded-2xl border border-marigold/40 bg-marigold/5 p-5"
    >
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-marigold/15 text-marigold-deep">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <p className="font-heading text-sm font-semibold text-foreground">Draft with AI</p>
          <p className="text-xs text-muted-foreground">Describe your issue and Claude will write your message.</p>
        </div>
      </div>

      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        data-testid="support-draft-prompt"
        rows={3}
        maxLength={2000}
        placeholder="e.g. My mother keeps missing her medication reminders and I need help fixing the notifications."
        className="mt-4 rounded-xl bg-background"
      />

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger data-testid="support-draft-model" className="rounded-xl sm:w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODELS.map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={generate}
          disabled={loading}
          data-testid="support-draft-generate"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 dark:bg-primary dark:text-primary-foreground"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {loading ? "Drafting…" : "Draft my message"}
        </button>
      </div>
    </div>
  );
}
